import React from 'react';
import UniversalIconBtn from '../headless/button/UniversalIconBtn';
import { useEditMode } from '../../hooks/useEditMode';
import { StyledDivComponent, StyledInputComponent } from '../../dto/StyleCompRepository';
import HeadlessInput from '../headless/input/HeadlessInput';
import Arrow from '../Arrow';
import styled from 'styled-components';
import { OrgWrapper } from './OrgDescription';
import searchIcon from '../../../asset/icon/see.png';
import searchNotIcon from '../../../asset/icon/see_not.png';
import { useDeviceStore } from '../../store/deviceStore';
import { DeviceType } from '../../enum/EnumRepository';
import HintSmallWrapper from '../headless/button/HintSmallWrapper';
import { getHintMessage } from '../../constants/hintMessages';
import { getContactsMessage } from '../../constants/pageMessages';
import { openNewTab } from '../../util/openNewTab';
import { parseHttpUrl } from '../../util/urlValidation';
import { useStandardAlertStore } from '../../store/portal/alertStore';
import { AlertPosition, AlertType } from '../../enum/EnumRepository';
import { getAlertMessage } from '../../constants/alertMessages';

export type ContactDefaultValueKey =
  | 'CONTACT_DEFAULT_INSTAGRAM'
  | 'CONTACT_DEFAULT_TWITTER'
  | 'CONTACT_DEFAULT_FACEBOOK'
  | 'CONTACT_DEFAULT_URL1';

interface OrgInputAnchorVisiProps {
  value: string;
  defaultValueKey: ContactDefaultValueKey;
  placeholder: string;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  inputStyle: StyledInputComponent;
  StyledDiv: StyledDivComponent;
  visible: boolean;
  changeVisible: () => void;
  changeVisibleMobile?: () => void;
}

const OrgInputAnchorVisi: React.FC<OrgInputAnchorVisiProps> = ({
  value,
  defaultValueKey,
  placeholder,
  handleChange,
  inputStyle,
  StyledDiv,
  visible,
  changeVisible,
  changeVisibleMobile,
}) => {
  const { isEditMode } = useEditMode();
  const { deviceType } = useDeviceStore();
  const { enqueueAlert } = useStandardAlertStore();
  const isMobile = deviceType === DeviceType.MOBILE;
  const defaultValue = getContactsMessage(defaultValueKey);

  const getUrl = (showAlert = false) => {
    const cleanValue = value.startsWith('@') ? value.slice(1) : value;

    switch (defaultValueKey) {
      case 'CONTACT_DEFAULT_INSTAGRAM':
        return isMobile
          ? `https://www.instagram.com/_u/${cleanValue}/`
          : `https://www.instagram.com/${cleanValue}/`;
      case 'CONTACT_DEFAULT_TWITTER':
        return `https://x.com/${cleanValue}/`;
      case 'CONTACT_DEFAULT_FACEBOOK':
        return isMobile
          ? `https://www.facebook.com/_u/${cleanValue}/`
          : `https://www.facebook.com/${cleanValue}/`;
      case 'CONTACT_DEFAULT_URL1':
        const parsedUrl = parseHttpUrl(value);
        if (!parsedUrl) {
          if (showAlert) {
            enqueueAlert({
              type: AlertType.ALERT,
              position: AlertPosition.TOP,
              content: getAlertMessage('INVALID_URL_FORMAT'),
            });
          }
          return null;
        }
        return parsedUrl.href;
      default:
        return null;
    }
  }

  const handleClick = () => {
    const url = getUrl(true);
    if (!url) return;

    openNewTab(url);
  }

  return (
    <>
      {isEditMode ? (
        <OrgWrapper>
          <InputWrapper $isVisible={visible}>
            <HeadlessInput
              type={'text'}
              value={value || ''}
              handleChange={handleChange}
              placeholder={placeholder}
              StyledInput={inputStyle}
            />
          </InputWrapper>
          <HintSmallWrapper content={visible ? getHintMessage('SET_TO_PRIVATE') : getHintMessage('SET_TO_PUBLIC')} toLeft={true}>
            <UniversalIconBtn
              size="fit"
              border={false}
              isTransparent
              backgroundImg={visible ? searchIcon : searchNotIcon}
              onClick={deviceType === DeviceType.MOBILE ? changeVisibleMobile : changeVisible}
            />
          </HintSmallWrapper>
        </OrgWrapper>
      ) : (
        visible ? (
          value ?
            <AnchorContainer>
              {isMobile ? (
                <StyledDiv onClick={handleClick}>
                  {value}
                  <Arrow />
                  <Indicator>{defaultValue}</Indicator>
                </StyledDiv>
              ) : (
                <StyledDiv
                  as="a"
                  href={getUrl() ?? undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {value}
                  <Arrow />
                  <Indicator>{defaultValue}</Indicator>
                </StyledDiv>
              )}
            </AnchorContainer>
            :
            <DefaultDiv>{defaultValue}</DefaultDiv>
        ) : null
      )}
    </>
  );
};

const InputWrapper = styled.div<{ $isVisible: boolean }>`
  width: 100%;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0.5)};
`;

const Indicator = styled.div`
  position: absolute;
  left: calc(100% + 0.2rem);
  bottom: 0;
  width: 0px;
  opacity: 0;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.color_Gray_05} !important;
  ${({ theme }) => theme.typography.Footer_01};
  font-style: italic;
  transition: all 0.5s ease, width 1s ease;
  white-space: nowrap;
`;

const AnchorContainer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 0.2rem;
  &:hover {
    ${Indicator} {
      width: 100px;
      opacity: 1;
    }
  }
`;

const DefaultDiv = styled.div`
  width: fit-content;
  height: 16px;
  color: ${({ theme }) => theme.colors.color_Gray_05} !important;
  ${({ theme }) => theme.typography.Body_04};

  background-color: ${({ theme }) => theme.colors.color_Gray_06};
`;

export default OrgInputAnchorVisi;
